import { type ReactNode, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChartCandlestick,
  ChevronDown,
  Gift,
  LayoutDashboard,
  UserRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { ChatFab } from '@/components/portal/ChatFab'
import { AccountMenu } from '@/components/portal/AccountMenu'
import { ComingSoonBadge } from '@/components/portal/ComingSoonBadge'
import { logoutFromCognito } from '@/lib/auth'
import { getClientId } from '@/lib/api-client'
import { isValidClientId } from '@/lib/client-id'
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus'
import { useAuthStore } from '@/store/auth'
import { cn } from '@/lib/utils'

export const NAV_ITEMS = [
  { to: '/dashboard', key: 'dashboard' },
  { to: '/funding', key: 'funding' },
  { to: '#', key: 'platform', disabled: true },
  { to: '#', key: 'rewards', disabled: true },
] as const

type NavItem = (typeof NAV_ITEMS)[number]

const NAV_ICONS: Record<NavItem['key'], LucideIcon> = {
  dashboard: LayoutDashboard,
  funding: Wallet,
  platform: ChartCandlestick,
  rewards: Gift,
}

interface PortalShellProps {
  children: ReactNode
}

/** Funding owns its history page, so /funds keeps "Pendanaan" highlighted. */
export function isActiveRoute(pathname: string, to: string) {
  if (to === '#') return false
  return pathname === to || (to === '/funding' && pathname === '/funds')
}

function isDisabled(item: NavItem): boolean {
  return 'disabled' in item && item.disabled
}

function usableName(value?: string | null) {
  const trimmed = value?.trim() ?? ''
  return trimmed && trimmed !== '-' ? trimmed : ''
}

function initialsOf(name: string) {
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return `${first}${last}`.toUpperCase()
}

function Avatar({ initials, className }: { initials: string; className?: string }) {
  return (
    <span className={cn('prod-avatar', className)} aria-hidden="true">
      {initials || <UserRound className="size-4" />}
    </span>
  )
}

export function PortalShell({ children }: PortalShellProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const user = useAuthStore((s) => s.user)
  const storedClientId = getClientId()
  const { data: status } = useOnboardingStatus(isValidClientId(storedClientId) ? storedClientId : null)
  const displayName = usableName(status?.display_name) || usableName(user?.name)
  const initials = initialsOf(displayName)
  const accountLabel = displayName ? `${t('common.accountMenu')}: ${displayName}` : t('common.accountMenu')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  async function handleLogout() {
    await logoutFromCognito()
    navigate('/login')
  }

  function navLinkProps(item: NavItem) {
    const active = isActiveRoute(location.pathname, item.to)
    const disabled = isDisabled(item)
    return {
      to: item.to,
      'aria-current': active ? ('page' as const) : undefined,
      'aria-disabled': disabled ? true : undefined,
      'aria-label': disabled ? `${t(`nav.${item.key}`)}, ${t('common.comingSoon')}` : undefined,
      onClick: disabled ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault() : undefined,
      active,
      disabled,
    }
  }

  return (
    <div className="prod-shell">
      <a href="#main-content" className="prod-skip-link">
        {t('common.skipToContent')}
      </a>
      <aside className="prod-shell__sidebar">
        <Link to="/dashboard" className="prod-shell__sidebar-logo">
          <img src="/regol/esafx-lockup.svg" alt="EsaFX" className="prod-shell__logo prod-shell__logo--lockup" />
        </Link>
        <nav className="prod-shell__sidebar-nav" aria-label={t('nav.main')}>
          {NAV_ITEMS.map((item) => {
            const { active, disabled, ...props } = navLinkProps(item)
            const Icon = NAV_ICONS[item.key]
            return (
              <Link
                key={item.key}
                {...props}
                className={cn('prod-shell__side-link', active && 'prod-shell__side-link--active')}
                title={disabled ? t('common.comingSoon') : undefined}
              >
                <Icon className="prod-shell__side-icon" aria-hidden="true" />
                <span className="prod-shell__side-label">{t(`nav.${item.key}`)}</span>
                {disabled ? <ComingSoonBadge compact className="prod-soon--chrome" /> : null}
              </Link>
            )
          })}
        </nav>
      </aside>

      <div className="prod-shell__body">
        <header className="prod-shell__topbar">
          <div className="prod-shell__topbar-inner">
            <Link to="/dashboard" className="prod-shell__topbar-logo">
              <img src="/regol/esafx-lockup.svg" alt="EsaFX" className="prod-shell__logo prod-shell__logo--lockup" />
            </Link>
            <button
              type="button"
              className="prod-shell__account-btn"
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              aria-label={accountLabel}
              onClick={() => setMenuOpen(true)}
            >
              <Avatar initials={initials} />
              {displayName ? <span className="prod-shell__account-name">{displayName}</span> : null}
              <ChevronDown className="prod-shell__account-chevron" aria-hidden="true" />
            </button>
          </div>
        </header>
        <main id="main-content" className="prod-shell__main" tabIndex={-1}>
          {children}
        </main>
      </div>

      <nav className="prod-tabbar" aria-label={t('nav.main')}>
        {NAV_ITEMS.map((item) => {
          const { active, disabled, ...props } = navLinkProps(item)
          const Icon = NAV_ICONS[item.key]
          return (
            <Link
              key={item.key}
              {...props}
              className={cn('prod-tabbar__item', active && 'prod-tabbar__item--active')}
            >
              <span className="prod-tabbar__icon-wrap">
                <Icon className="prod-tabbar__icon" aria-hidden="true" />
                {disabled ? <ComingSoonBadge compact className="prod-tabbar__soon" /> : null}
              </span>
              <span className="prod-tabbar__label">{t(`nav.${item.key}`)}</span>
            </Link>
          )
        })}
        <button
          type="button"
          className={cn('prod-tabbar__item', menuOpen && 'prod-tabbar__item--active')}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <span className="prod-tabbar__icon-wrap">
            <Avatar initials={initials} className="prod-avatar--tab" />
          </span>
          <span className="prod-tabbar__label">{t('common.account')}</span>
        </button>
      </nav>

      <AccountMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onLogout={() => void handleLogout()}
        name={displayName}
        initials={initials}
      />
      <ChatFab />
    </div>
  )
}
